import Grow from "@mui/material/Grow";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import InputAdornment from "@mui/material/InputAdornment";
import GoogleIcon from "@mui/icons-material/Google";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { IAddressModel } from "../../types/types";
import { removeCounty } from "../../utils/common";

// Debounce utility function
const debounce = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

interface AddressInputProps {
  value: IAddressModel | null;
  onChange: (value: IAddressModel) => void;
  onCoordinatesChange?: (lat: number, lng: number) => void;
  onAreaChange?: (address: IAddressModel) => void;
  googleOnly?: boolean; // Initial mode setting
  onValidationChange?: (isValid: boolean) => void;
  onModeChange?: (isGoogleMode: boolean) => void; // Expose Google mode state to parent
}

const AddressInput: React.FC<AddressInputProps> = ({
  value,
  onChange,
  onCoordinatesChange,
  onAreaChange,
  googleOnly = true,
  onValidationChange,
  onModeChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isInitialMount = useRef(true);
  const [apiLoaded, setApiLoaded] = useState(false);
  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showPredictions, setShowPredictions] = useState(false);
  const [isGoogleMode, setIsGoogleMode] = useState(true); // Default to Google mode
  const isGoogleModeRef = useRef(true); // Track mode synchronously
  // const [hasUserSelectedAddress, setHasUserSelectedAddress] = useState(false);
  const [isValidGoogleAddress, setIsValidGoogleAddress] = useState(false);
  // const [isLoadingSchoolDistrict, setIsLoadingSchoolDistrict] = useState(false);

  // Helper function to update both state and ref
  const updateGoogleMode = useCallback(
    (newMode: boolean, defer: boolean = false) => {
      setIsGoogleMode(newMode);
      isGoogleModeRef.current = newMode;
      if (onModeChange) {
        if (defer) {
          // Defer for initialization to avoid render phase update
          setTimeout(() => {
            onModeChange(newMode);
          }, 0);
        } else {
          // Immediate for user interactions
          onModeChange(newMode);
        }
      }
    },
    [onModeChange]
  );

  useEffect(() => {
    const checkApiLoaded = () => {
      if (window.google?.maps?.places) {
        setApiLoaded(true);
        return true;
      }
      return false;
    };

    if (checkApiLoaded()) return;

    const interval = setInterval(() => {
      checkApiLoaded();
    }, 500);

    const timeout = setTimeout(() => {
      clearInterval(interval);
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  // Click outside handler to hide predictions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setPredictions([]);
        setShowPredictions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Initialize mode based on existing address data ONLY on initial mount
  useEffect(() => {
    // Only auto-detect on initial mount AND only if there's actual data
    if (isInitialMount.current) {
      if (
        value &&
        value.formattedAddress &&
        value.formattedAddress.trim() !== ""
      ) {
        // Check if the existing address has Google Places characteristics
        const hasCoordinates =
          typeof value.latitude === "number" &&
          typeof value.longitude === "number";
        const hasStructuredData =
          (value.street && value.street.trim() !== "") ||
          (value.state && value.state.trim() !== "") ||
          (value.zip && value.zip.trim() !== "") ||
          (value.county && value.county.trim() !== "");

        // Auto-detect mode based on address data
        const isExistingGoogleAddress = hasCoordinates && hasStructuredData;

        // Set the mode based on the loaded address data (only on initial load)
        if (isExistingGoogleAddress) {
          updateGoogleMode(true, true); // Defer the callback
          setIsValidGoogleAddress(true);
        } else {
          // Has text but no Google data - it's free text
          updateGoogleMode(false, true); // Defer the callback
          setIsValidGoogleAddress(true);
        }

        isInitialMount.current = false;
      }
      // If no value or empty value, keep default mode (Google mode) and wait
      // for the value to load (e.g. edit form resets after fetching)
    }
  }, [value, updateGoogleMode]);

  // Update validation state when mode or address changes (but don't change mode)
  useEffect(() => {
    if (value) {
      const hasCoordinates =
        typeof value.latitude === "number" &&
        typeof value.longitude === "number";
      const hasStructuredData =
        (value.street && value.street.trim() !== "") ||
        (value.state && value.state.trim() !== "") ||
        (value.zip && value.zip.trim() !== "") ||
        (value.county && value.county.trim() !== "");

      const isExistingGoogleAddress = hasCoordinates && hasStructuredData;

      if (isGoogleMode && isExistingGoogleAddress) {
        setIsValidGoogleAddress(true);
      } else if (!isGoogleMode) {
        // In free text mode, always valid
        setIsValidGoogleAddress(true);
      } else {
        // Google mode but no valid Google data
        setIsValidGoogleAddress(false);
      }
    } else {
      // No address data
      setIsValidGoogleAddress(!isGoogleMode);
    }
  }, [value, isGoogleMode]);

  const handleSearch = useCallback(
    async (input: string) => {
      input = removeCounty(input);
      if (!window.google?.maps?.places || !apiLoaded || input.length < 3) {
        setPredictions([]);
        return;
      }

      try {
        setIsLoading(true);
        const { AutocompleteService } = (await google.maps.importLibrary(
          "places"
        )) as google.maps.PlacesLibrary;
        const service = new AutocompleteService();

        service.getPlacePredictions(
          {
            input,
            types: ["address"], //"address",

            // componentRestrictions: { country: "us" },
          },
          (predictions, status) => {
            if (
              status === google.maps.places.PlacesServiceStatus.OK &&
              predictions
            ) {
              setPredictions(predictions || []);
            } else {
              setPredictions([]);
            }
            setIsLoading(false);
          }
        );
      } catch (error) {
        console.error("Error loading places:", error);
        setPredictions([]);
        setIsLoading(false);
      }
    },
    [apiLoaded]
  );

  // Debounced search function
  const debouncedSearch = useCallback(
    (input: string) => {
      const debouncedFn = debounce((searchInput: string) => {
        if (searchInput.length >= 3 && isGoogleMode) {
          handleSearch(searchInput);
          setShowPredictions(true);
        } else {
          setPredictions([]);
          setShowPredictions(false);
        }
      }, 300);
      debouncedFn(input);
    },
    [isGoogleMode, handleSearch]
  );

  const getPlaceDetails = (placeId: string) => {
    if (!window.google?.maps?.places) return;

    // Create a dummy map element (required by PlacesService)
    const mapDiv = document.createElement("div");
    const map = new google.maps.Map(mapDiv);
    const service = new google.maps.places.PlacesService(map);

    service.getDetails(
      {
        placeId: placeId,
        fields: ["geometry", "address_components", "formatted_address"],
      },
      (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          let street = "";
          let city = "";
          let state = "";
          let zip = "";
          let county = "";
          let country = "";
          let subpremise = "";
          place.address_components?.forEach((component) => {
            const types = component.types;

            if (types.includes("street_number")) {
              street = component.long_name;
            } else if (types.includes("route")) {
              street = street
                ? `${street} ${component.long_name}`
                : component.long_name;
            } else if (types.includes("subpremise")) {
              subpremise = component.long_name;
            } else if (types.includes("locality")) {
              city = component.long_name;
            } else if (types.includes("sublocality") && !city) {
              city = component.long_name;
            } else if (types.includes("administrative_area_level_1")) {
              state = component.short_name?.toUpperCase();
            } else if (types.includes("postal_code")) {
              zip = component.long_name;
            } else if (types.includes("country")) {
              country = component.short_name;
            } else if (types.includes("administrative_area_level_2")) {
              county = component.long_name;
              if (county && county.endsWith(" County")) {
                county = county.replace(" County", "");
              }
            }
          });

          const latitude = place.geometry?.location?.lat();
          const longitude = place.geometry?.location?.lng();

          const address: IAddressModel = {
            street: street || "",
            city: city || "",
            state: state || "",
            zip: zip || "",
            county: county || "",
            formattedAddress: place.formatted_address || "",
            latitude: latitude,
            longitude: longitude,
            country: country || "",
            subpremise: subpremise || "",
          };
          onChange(address);

          // if (onAreaChange) {
          //   onAreaChange(address);
          // }

          if (onCoordinatesChange && latitude && longitude) {
            onCoordinatesChange(latitude, longitude);
          }
        }
      }
    );
  };

  const handleInputChange = (input: string) => {
    // User interaction - stop auto-detecting mode from value
    isInitialMount.current = false;

    // Reset validation states when user types
    setIsValidGoogleAddress(false);

    let newValue: IAddressModel;

    if (isGoogleMode) {
      // In Google mode, clear structured data until user selects from suggestions
      newValue = {
        street: "",
        city: "",
        state: "",
        zip: "",
        county: "",
        country: "",
        formattedAddress: input,
        latitude: undefined,
        longitude: undefined,
      };
      debouncedSearch(input);
    } else {
      // In free text mode, clear all address details except the formatted address
      newValue = {
        street: "",
        city: "",
        state: "",
        zip: "",
        county: "",
        country: "",
        formattedAddress: input,
        latitude: undefined,
        longitude: undefined,
      };
    }

    onChange(newValue);

    // Defer validation change callback to avoid render phase update
    setTimeout(() => {
      if (onValidationChange) {
        onValidationChange(isGoogleMode ? false : true);
      }
    }, 0);
  };

  const handleModeToggle = () => {
    isInitialMount.current = false;
    const newMode = !isGoogleMode;
    updateGoogleMode(newMode, false); // Don't defer for user interaction

    setIsValidGoogleAddress(!newMode);

    // Clear predictions when switching modes
    setPredictions([]);
    setShowPredictions(false);

    // If switching to free text mode, clear all address details except formatted address
    if (!newMode && value) {
      const clearedAddress: IAddressModel = {
        street: "",
        city: "",
        state: "",
        zip: "",
        county: "",
        country: "",
        formattedAddress: value.formattedAddress || "",
        latitude: undefined,
        longitude: undefined,
      };
      onChange(clearedAddress);
    }

    // Defer validation change callback to avoid render phase update
    setTimeout(() => {
      if (onValidationChange) {
        onValidationChange(newMode ? false : true);
      }
    }, 0);
  };

  const handlePredictionClick = (
    prediction: google.maps.places.AutocompletePrediction
  ) => {
    isInitialMount.current = false;
    const updatedAddress = {
      ...value,
      formattedAddress: prediction.description,
    } as IAddressModel;

    onChange(updatedAddress);

    setIsValidGoogleAddress(true);

    // Always get detailed info and coordinates
    getPlaceDetails(prediction.place_id);
    setPredictions([]);
    setShowPredictions(false);

    // Defer validation change callback to avoid render phase update
    setTimeout(() => {
      if (onValidationChange) {
        onValidationChange(true);
      }
    }, 0);
  };

  // Determine if we should show error state
  const showError =
    isGoogleMode &&
    value?.formattedAddress &&
    value.formattedAddress.length > 0 &&
    !isValidGoogleAddress;

  return (
    <Box ref={containerRef} sx={{ position: "relative" }}>
      <Tooltip
        title={
          isGoogleMode
            ? "Using Google Address (click for free text)"
            : "Using Free Text (click for Google Address)"
        }
        arrow
      >
        <TextField
          value={value?.formattedAddress || ""}
          onChange={(e) => handleInputChange(e.target.value)}
          label={"Address"}
          fullWidth
          variant="outlined"
          error={!!showError}
          helperText={
            showError
              ? ""
              : isGoogleMode
              ? apiLoaded
                ? "To disable mapping, click the Google icon"
                : "Google Maps not loaded - switch to free text"
              : "To enable mapping, click the Google icon"
          }
          slotProps={{
            inputLabel: {
              shrink: true,
            },
            input: {
              notched: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleModeToggle}
                    size="small"
                    sx={{
                      color: isGoogleMode ? "#4285F4" : "action.disabled",
                    }}
                  >
                    <GoogleIcon />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      </Tooltip>
      <Grow
        in={
          predictions.length > 0 &&
          !isLoading &&
          showPredictions &&
          isGoogleMode
        }
      >
        <div
          style={{
            position: "absolute",
            zIndex: 2,
            width: "100%",
            marginTop: "4px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            maxHeight: "200px",
            overflowY: "auto",
            backgroundColor: "#fff",
            border: "1px solid #e0e0e0",
            borderRadius: "4px",
          }}
        >
          {predictions.map((prediction) => (
            <div
              key={prediction.place_id}
              style={{
                padding: "12px",
                cursor: "pointer",
                backgroundColor: "#fff",
                borderBottom: "1px solid #f0f0f0",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f5f5f5";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#fff";
              }}
              onClick={() => handlePredictionClick(prediction)}
            >
              {prediction.description}
            </div>
          ))}
        </div>
      </Grow>
    </Box>
  );
};

export default AddressInput;
